// TMOS User Position List Component
// Date: December 19, 2025
// Purpose: Component for displaying users with their position assignments

import React, { useState, useEffect } from 'react';
import { userPositionService, UserWithPositions } from '@/services/userPositionService';
import { businessPositionService, BusinessPosition } from '@/services/businessPositionService';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { UserPositionAssignmentModal } from './UserPositionAssignmentModal';
import { useLanguage } from '@/state/language/LanguageProvider';
import { Select } from '@/components/ui/Select';
import { toast } from 'react-hot-toast';
import { UserIcon, BriefcaseIcon } from '@heroicons/react/24/outline';

interface UserPositionListProps {
  tenantId?: string;
  onUserSelect?: (user: UserWithPositions) => void;
}

export const UserPositionList: React.FC<UserPositionListProps> = ({
  tenantId,
  onUserSelect
}) => {
  const { t } = useLanguage();
  const [users, setUsers] = useState<UserWithPositions[]>([]);
  const [positions, setPositions] = useState<BusinessPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterPositionId, setFilterPositionId] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<UserWithPositions | null>(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadData();
  }, [tenantId, filterPositionId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load positions for filter dropdown
      const allPositions = await businessPositionService.getActivePositions();
      setPositions(allPositions);

      // Load users
      let allUsers: UserWithPositions[];
      if (filterPositionId && filterPositionId !== 'all') {
        allUsers = await userPositionService.getUsersByPosition(filterPositionId, tenantId);
      } else {
        allUsers = await userPositionService.getUsersWithPositions(tenantId);
      }

      setUsers(allUsers);
    } catch (error: any) {
      console.error('Error loading user positions:', error);
      toast.error(error.message || t('position.loadError') || 'Failed to load user positions');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignPosition = (user: UserWithPositions) => {
    setSelectedUser(user);
    setShowAssignmentModal(true);
  };

  const handleAssignmentSuccess = () => {
    loadData();
  };

  const getPositionName = (positionId: string): string => {
    const position = positions.find(p => p.id === positionId);
    return position?.position_name || positionId;
  };

  const filteredUsers = searchTerm
    ? users.filter(user => {
        const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
        const email = user.email.toLowerCase();
        const term = searchTerm.toLowerCase();
        return fullName.includes(term) || email.includes(term);
      })
    : users;

  if (loading) {
    return (
      <div className="text-center py-8 text-[var(--color-secondary-text)]">
        {t('common.loading') || 'Loading users...'}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-4 items-end">
        <div className="flex-1">
          <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
            {t('common.search') || 'Search'}
          </label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('position.searchUsers') || 'Search by name or email...'}
            className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-secondary-bg)] text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-bg)]"
          />
        </div>
        <div className="w-64">
          <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
            {t('position.filterByPosition') || 'Filter by Position'}
          </label>
          <Select
            value={filterPositionId}
            onChange={(e) => setFilterPositionId(e.target.value)}
            className="w-full"
            options={[
              { value: 'all', label: t('position.allPositions') || 'All Positions' },
              ...positions.map(position => ({ value: position.id, label: position.position_name }))
            ]}
          />
        </div>
      </div>

      {/* User List */}
      <div className="space-y-3">
        {filteredUsers.length === 0 ? (
          <div className="text-center py-8 text-[var(--color-secondary-text)]">
            {t('position.noUsersFound') || 'No users found'}
          </div>
        ) : (
          filteredUsers.map(user => (
            <div
              key={user.id}
              className="p-4 bg-[var(--color-secondary-bg)] rounded-lg border border-[var(--color-border)] hover:border-[var(--color-primary-bg)] transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <UserIcon className="w-5 h-5 text-[var(--color-secondary-text)]" />
                    <div>
                      <div className="font-semibold text-[var(--color-surface-text)]">
                        {user.first_name} {user.last_name}
                      </div>
                      <div className="text-sm text-[var(--color-secondary-text)]">
                        {user.email}
                      </div>
                    </div>
                  </div>

                  {/* Positions */}
                  {user.positions.length > 0 ? (
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <BriefcaseIcon className="w-4 h-4 text-[var(--color-secondary-text)]" />
                      {user.positions.map(position => (
                        <Badge
                          key={position.id}
                          variant={position.is_primary ? 'success' : 'default'}
                          className="cursor-pointer"
                          onClick={() => onUserSelect?.(user)}
                        >
                          {getPositionName(position.position_id)}
                          {position.is_primary && ' (Primary)'}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-2 text-sm text-[var(--color-secondary-text)] italic">
                      {t('position.noPositionsAssigned') || 'No positions assigned'}
                    </div>
                  )}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleAssignPosition(user)}
                >
                  {user.positions.length > 0
                    ? t('position.editPositions') || 'Edit Positions'
                    : t('position.assignPosition') || 'Assign Position'}
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Assignment Modal */}
      {selectedUser && (
        <UserPositionAssignmentModal
          isOpen={showAssignmentModal}
          onClose={() => {
            setShowAssignmentModal(false);
            setSelectedUser(null);
          }}
          userId={selectedUser.id}
          userName={`${selectedUser.first_name} ${selectedUser.last_name}`}
          userEmail={selectedUser.email}
          existingPositions={selectedUser.positions}
          onSuccess={handleAssignmentSuccess}
        />
      )}
    </div>
  );
};

